import React, { useMemo, useState, useCallback } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { Colors } from 'libs/end-to-end/src/types/branding/color';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { ChartLegendButton } from 'apps/sigma2-ui/src/pages/ProgressPage/ChartLegendButton';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useFormatCellValue } from 'apps/sigma2-ui/src/explore-section/hooks/useFormatCellValue';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { SingleMetricTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GenericTableViewTooltip';
import { useTableViewXAxisLabel } from 'apps/sigma2-ui/src/explore-section/hooks/useTableViewXAxisLabel';

/**
 * Line chart component for visualizing TableView data using Nivo.
 *
 * Features:
 * - Converts table data to Nivo line chart format
 * - Shows trends over categories (groupBy dimension)
 * - Custom tooltip with formatted values
 * - Consistent theming with app design system
 * - First column used as x-axis, selected metric as y-axis
 *
 * @example
 * ```tsx
 * <LineChartMode
 *   data={tableData}
 *   visibleRows={visibleRows}
 *   viewConfiguration={viewConfiguration}
 * />
 * ```
 */
export const LineChartMode: React.FC<TableViewModeArguments> = ({
  data,
  visibleRows,
  viewConfiguration,
}) => {
  const { selectedMetric } = viewConfiguration;
  const { getColumnDisplayName } = useGetColumnDisplayName();

  const overallDisplayAverage = useMemo(() => {
    return viewConfiguration.overallDisplayMode !== 'count';
  }, [viewConfiguration.overallDisplayMode]);

  // Transform table data to Nivo line chart format
  const allLineChartData = useMemo(() => {
    if (!selectedMetric) {
      return [];
    }

    // Handle both new and old data structures
    const columnHeaders = data.columnHeaders;
    const rows = overallDisplayAverage ? data.rows : data.numeratorSizes;

    const selectedMetricIndex = columnHeaders.indexOf(selectedMetric);
    if (selectedMetricIndex === -1) {
      return [];
    }

    const rowsMeta = data.rowsMeta;
    // Check if we have secondary breakdown data
    if (rowsMeta.some((row) => row.secondaryBreakdownLevel !== undefined)) {
      // Get all unique primary values in the correct order
      const allPrimaryValues = Array.from(
        new Set(rowsMeta.map((row) => row.groupByLevel)),
      ).sort();

      // Get all unique secondary values and sort them intelligently
      const allSecondaryValues = Array.from(
        new Set(rowsMeta.map((row) => row.secondaryBreakdownLevel)),
      ).filter((value) => value !== undefined);

      // Sort secondary values intelligently (same logic as Python script)
      const sortedSecondaryValues = allSecondaryValues.sort((a, b) => {
        try {
          // Attempt numeric sorting
          const aNum = parseFloat(a);
          const bNum = parseFloat(b);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
        } catch {
          // Fall through to alphabetical sorting
        }
        // Alphabetical sorting for non-numeric values
        return a.localeCompare(b);
      });

      return sortedSecondaryValues.map((secondaryValue) => {
        // Create a map of primary values to their data for this secondary value
        const dataMap = new Map<
          string,
          {
            groupByLevel: string;
            secondaryBreakdownLevel: string;
            metrics: string[];
          }
        >();
        rowsMeta.forEach((row, rowIndex) => {
          if (row.secondaryBreakdownLevel === secondaryValue) {
            dataMap.set(row.groupByLevel, {
              groupByLevel: row.groupByLevel,
              secondaryBreakdownLevel: row.secondaryBreakdownLevel,
              metrics: rows[rowIndex].slice(1).map(String),
            });
          }
        });

        // Create line data for all primary values in order, filling missing values
        const lineData = allPrimaryValues
          .map((primaryValue, index) => {
            const row = dataMap.get(primaryValue);
            if (row) {
              const metricValue = row.metrics[selectedMetricIndex];
              const numericValue = safeParseFloat(metricValue);
              return {
                x: primaryValue,
                y: numericValue || 0,
                rawValue: metricValue,
                rowIndex: index,
              };
            } else {
              // No data for this primary value, create null point
              return {
                x: primaryValue,
                y: null, // This will create a gap in the line
                rawValue: '',
                rowIndex: index,
              };
            }
          })
          .filter(
            (_, index) =>
              visibleRows.length === 0 || visibleRows.includes(index),
          );

        return {
          id: secondaryValue, // Each line represents a secondary breakdown value
          data: lineData,
        };
      });
    }

    // Original single line logic for primary data only
    const lineData = rows
      .map((row, index) => {
        const categoryValue = row[0]; // First column is the category (groupBy dimension)
        const metricValue = row[selectedMetricIndex + 1]; // +1 because row[0] is groupBy dimension
        const numericValue = safeParseFloat(String(metricValue ?? 0));

        return {
          x: categoryValue,
          y: numericValue || 0,
          rawValue: metricValue,
          rowIndex: index,
        };
      })
      .filter(
        (_, index) => visibleRows.length === 0 || visibleRows.includes(index),
      );

    return [
      {
        id: getColumnDisplayName(selectedMetric),
        data: lineData,
      },
    ];
  }, [
    selectedMetric,
    data.columnHeaders,
    data.rows,
    data.numeratorSizes,
    data.rowsMeta,
    overallDisplayAverage,
    getColumnDisplayName,
    visibleRows,
  ]);

  // Extract unique series IDs for legend buttons
  const allSeriesIds = useMemo(() => {
    return allLineChartData.map((series) => series.id);
  }, [allLineChartData]);

  // Store selected series. When list is empty all series are displayed.
  const [selectedSeries, setSelectedSeries] = useState<string[]>([]);

  // Callback to toggle a selected series
  const handleOnClickSeries = useCallback(
    (toggledSeries: string) => {
      setSelectedSeries((series) => {
        if (series.includes(toggledSeries)) {
          return series.filter((d) => d !== toggledSeries);
        } else {
          return allSeriesIds.filter(
            (s) => series.includes(s) || s === toggledSeries,
          );
        }
      });
    },
    [allSeriesIds],
  );

  // Callback to clear selected series (select all)
  const handleOnClickAll = useCallback(() => {
    setSelectedSeries([]);
  }, []);

  // Compute which series to display
  const computedSelectedSeries = useMemo(() => {
    if (selectedSeries.length === 0) {
      return allSeriesIds;
    }
    return selectedSeries;
  }, [selectedSeries, allSeriesIds]);

  // Filter line chart data based on selected series
  const lineChartData = useMemo(() => {
    return allLineChartData.filter((series) =>
      computedSelectedSeries.includes(series.id),
    );
  }, [allLineChartData, computedSelectedSeries]);

  const { formatCellValue } = useFormatCellValue();

  const selectedMetricIndex = useMemo(() => {
    if (!selectedMetric) {
      return -1;
    }
    return data.columnHeaders.indexOf(selectedMetric);
  }, [selectedMetric, data.columnHeaders]);

  const xAxisLabel = useTableViewXAxisLabel({
    data,
    viewConfiguration,
  });

  if (!selectedMetric) {
    return null;
  }

  return (
    <Spacing size="md" direction="vertical">
      <div className="w-full h-[400px] bg-white">
        <ResponsiveLine
          data={lineChartData}
          margin={{ top: 80, right: 50, bottom: 120, left: 100 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: xAxisLabel,
            legendPosition: 'middle',
            legendOffset: 100,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: getColumnDisplayName(selectedMetric),
            legendPosition: 'middle',
            legendOffset: -80,
            format: (s: number) => {
              return overallDisplayAverage
                ? formatCellValue({
                    rawValue: s.toString(),
                    header: selectedMetric,
                    units: viewConfiguration.units,
                  })
                : s.toString();
            },
          }}
          colors={({ id }) => {
            // Use different colors for each line based on original series order
            const colorIndex = allLineChartData.findIndex(
              (line) => line.id === id,
            );
            return ConfigColors[Colors[colorIndex % Colors.length]].Primary.raw;
          }}
          lineWidth={3}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          enableArea={false}
          useMesh={true}
          legends={
            allSeriesIds.length > 1
              ? [] // Disable default Nivo legend when we have ChartLegendButton
              : [
                  {
                    anchor: 'top-left',
                    direction: 'row',
                    justify: false,
                    translateX: 10,
                    translateY: -20,
                    itemsSpacing: 20,
                    itemDirection: 'left-to-right',
                    itemWidth: 120,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1,
                        },
                      },
                    ],
                  },
                ]
          }
          tooltip={({ point }) => {
            const cellValue = data.rows[point.index][selectedMetricIndex + 1];
            const rowMeta = data.rowsMeta[point.index];
            const isSecondaryBreakdown =
              rowMeta.secondaryBreakdownLevel !== undefined;
            const label = isSecondaryBreakdown
              ? `${rowMeta.groupByLevel} - ${rowMeta.secondaryBreakdownLevel}`
              : rowMeta.groupByLevel;

            return (
              <SingleMetricTooltip
                metricColumn={selectedMetric}
                percentageValue={Number(cellValue)}
                levelDisplayName={label}
                totalSubjects={
                  data.sampleSizes[point.index][selectedMetricIndex + 1]
                }
                numeratorSize={
                  data.numeratorSizes[point.index]?.[selectedMetricIndex + 1] ??
                  0
                }
                units={viewConfiguration.units}
                overallDisplayMode={viewConfiguration.overallDisplayMode}
              />
            );
          }}
          theme={{
            text: {
              fontFamily: KARLA_FONT_FAMILY,
              fontSize: 13,
            },
            axis: {
              domain: {
                line: {
                  stroke: '#B8BABC',
                  strokeWidth: 1,
                },
              },
              ticks: {
                line: {
                  stroke: '#B8BABC',
                  strokeWidth: 1,
                },
              },
            },
            background: '#ffffff',
          }}
          motionConfig="gentle"
          animate={true}
        />
      </div>
      {allSeriesIds.length > 1 && (
        <Box
          display="flex"
          flexWrap="wrap"
          flexDirection="row"
          gap="xs"
          justifyContent="center"
        >
          <ChartLegendButton
            label="All"
            id="All"
            focused={selectedSeries.length === 0}
            onClick={handleOnClickAll}
            hasTransparentBorder={false}
          />
          {allSeriesIds.map((seriesId, index) => (
            <ChartLegendButton
              key={seriesId}
              label={seriesId}
              id={seriesId}
              iconColor={ConfigColors[Colors[index % Colors.length]].Primary.bg}
              focused={computedSelectedSeries.includes(seriesId)}
              onClick={handleOnClickSeries}
              hasTransparentBorder={false}
            />
          ))}
        </Box>
      )}
    </Spacing>
  );
};
